/* ==========================================================================
   Casemira C3 - page collection
   vague 1 : la barre de filtres de l'ordinateur (≥ 1000 px)
   vague 2 : la feuille inférieure du téléphone (< 1000 px)
   --------------------------------------------------------------------------
   Les deux mondes partagent le MÊME formulaire de facettes, la même barre
   Liquid et les mêmes gestes composés ; ils ne diffèrent que par la mise en
   scène (panneaux ancrés d'un côté, feuille à deux crans de l'autre) et par
   la mémoire de densité (4 | 6 contre 2 | 1).

   Principe directeur : la barre vit dans `#FacetFiltersContainer`, que
   `assets/facets.js` remplace intégralement à chaque rendu AJAX. Tout son
   contenu (compteurs, cases cochées, chips, « Voir N tapis ») est donc
   recalculé par Liquid, et ce script n'a JAMAIS à synchroniser un état.
   Il ne lui reste que trois choses à faire :
     1. ouvrir et fermer les panneaux (et rouvrir celui qui l'était après un
        rendu, puisque le DOM a été recréé) ;
     2. traduire les gestes composés - un palier de taille coche toutes ses
        cotes, une chip coche sa case, une tranche de prix remplit min/max -
        en UN seul `change` sur le formulaire de facettes ;
     3. animer le replacement des cartes qui survivent au changement de
        filtre (FLIP), le fondu étant posé par facets.js.

   Tous les écouteurs sont délégués au document et posés UNE fois : ils
   survivent donc aux re-rendus sans aucun rebranchement.
   ========================================================================== */

(function () {
  'use strict';

  var MQ = '(min-width: 1000px)';
  var DENSITY_KEY = 'casemira:collection-density';
  var DENSITY_KEY_M = 'casemira:collection-density-m';
  var PANEL_SELECTOR = '.cmc-panel';

  /* Seuils du glissement : ceux du lecteur vidéo de la fiche produit
     (assets/casemira-videos.js), pour que les deux surfaces se manipulent de la
     même main. SLOP = au-delà, ce n'est plus un tap. */
  var DRAG_SLOP = 6;
  var DRAG_DISTANCE = 80; /* px vers le bas : on change de cran, ou on ferme  */
  var DRAG_VELOCITY = 0.5; /* px/ms : un geste vif vaut une longue distance   */
  var DRAG_UP = 40; /* px vers le haut depuis le cran 1 : plein écran         */
  var DRAG_DISMISS = 240; /* px vers le bas depuis le plein écran : on ferme  */
  var DRAG_RUBBER = 56; /* course maximale au-delà du plein écran            */
  /* VAGUE 21 · la fenêtre sur laquelle se lit un LANCER, en millisecondes.
     Même valeur et même raison que la mini-fiche (§ 6.3 de
     `casemira-collection-plus.js`) : au-delà de 90 ms, ce n'est plus un
     lancer, c'est un déplacement. */
  var DRAG_WINDOW = 90;

  /* Groupes ouverts d'office dans la feuille (brief point 3, maquette m-02). */
  var SHEET_OPEN_BY_DEFAULT = ['taille', 'prix'];

  /* État qui doit survivre au remplacement du DOM de la barre. */
  var openPanelKey = null;
  var refocusKey = null;

  /* Capture FLIP entre le début et la fin d'un rendu AJAX. */
  var flipMap = null;
  var flipScrollY = 0;

  /* ── État de la feuille mobile ────────────────────────────────────────────
     Tout ce qui doit survivre au remplacement du DOM par `facets.js` vit ici,
     ou sur `<html>` (les classes `cmm-*`), que le rendu AJAX ne touche pas. */
  var sheetCran = 0; /* 0 fermé · 1 premier cran (62 %) · 2 plein écran */
  var sheetGroups = null; /* clés des groupes dépliés, ordre indifférent */
  var sheetScrollTop = 0;
  var sheetPushed = false; /* un état d'historique à nous est-il empilé ? */
  var lastRenderedUrl = window.location.href; /* dernière URL écrite par facets.js */

  var desktopQuery = window.matchMedia ? window.matchMedia(MQ) : null;
  var reducedQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function isDesktop() {
    return desktopQuery ? desktopQuery.matches : window.innerWidth >= 1000;
  }

  function isReduced() {
    return reducedQuery ? reducedQuery.matches : false;
  }

  function bar() {
    return document.querySelector('casemira-filter-bar');
  }

  function barForm() {
    var el = bar();
    return el ? el.querySelector('form[is="facet-form"]') : null;
  }

  /* Un seul point de sortie : on repasse la main à facets.js en simulant le
     `change` que le formulaire attend. Le thème s'occupe de l'URL, du fetch,
     du rendu et de l'historique. */
  function submitForm() {
    var form = barForm();
    if (!form) return;
    form.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ── Panneaux ─────────────────────────────────────────────────────────── */

  function panels() {
    var el = bar();
    return el ? el.querySelectorAll(PANEL_SELECTOR) : [];
  }

  function placePanel(panel) {
    var el = bar();
    if (!el || !panel) return;

    /* VAGUE 19 · L'ANCRE EST LA BARRE, comme en CSS. `.cmc-panel` est
       positionné par rapport à `.cmc__bar` depuis que celle-ci porte
       `position: relative` (`casemira-collection.css` § 3, et le pourquoi y est
       écrit) : mesurer le décalage horizontal sur `.cmc__sticky` ferait deux
       origines pour un seul objet. Les deux boîtes ont aujourd'hui les mêmes
       bords - c'est justement ce qu'on ne veut pas avoir à supposer. Repli sur
       le socle si la barre manque : sur téléphone, le panneau est en
       `position: static` et l'inset ne s'applique pas. */
    var host = el.querySelector('.cmc__bar') || el.querySelector('.cmc__sticky');
    var button = el.querySelector('[data-cmc-toggle="' + panel.dataset.cmcPanel + '"]');
    if (!host || !button) return;

    var hostRect = host.getBoundingClientRect();
    var buttonRect = button.getBoundingClientRect();
    var width = panel.offsetWidth;
    var left = buttonRect.left - hostRect.left;

    /* Le panneau reste dans la largeur utile de la page : il s'aligne sur son
       bouton tant qu'il tient, puis se cale sur la marge droite. */
    var maxLeft = Math.max(0, hostRect.width - width);
    if (left > maxLeft) left = maxLeft;
    if (left < 0) left = 0;

    panel.style.insetInlineStart = Math.round(left) + 'px';

    /* Hauteur : ce qui reste sous la barre, et non une fraction fixe de l'écran.
       Ouvert en haut de page, la barre est encore à 525 px du haut : un plafond
       de 70 vh ferait sortir le pied du panneau de l'écran, et « Voir N tapis »
       deviendrait inatteignable. On mesure donc la place réellement disponible,
       plancher à 240 px pour ne jamais réduire le panneau à un trait. */
    var top = panel.getBoundingClientRect().top;
    var available = window.innerHeight - top - 16;
    var ceiling = window.innerHeight * 0.7;
    panel.style.maxHeight = Math.round(Math.max(200, Math.min(available, ceiling))) + 'px';
  }

  function closePanel() {
    var el = bar();
    openPanelKey = null;
    if (!el) return;

    el.querySelectorAll(PANEL_SELECTOR).forEach(function (panel) {
      panel.classList.remove('is-open');
    });
    el.querySelectorAll('[data-cmc-toggle]').forEach(function (button) {
      button.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
    });
  }

  function openPanel(key, options) {
    var el = bar();
    if (!el) return;

    var panel = el.querySelector(PANEL_SELECTOR + '[data-cmc-panel="' + key + '"]');
    if (!panel) {
      openPanelKey = null;
      return;
    }

    closePanel();
    openPanelKey = key;
    placePanel(panel);
    panel.classList.add('is-open');

    var button = el.querySelector('[data-cmc-toggle="' + key + '"]');
    if (button) {
      button.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
    }

    if (options && options.focus) {
      var first = panel.querySelector('input:not([type="hidden"]), button');
      if (first) first.focus();
    }
  }

  /* ── Densité de grille ────────────────────────────────────────────────── */

  function currentDensity() {
    var value = document.documentElement.getAttribute('data-cmc-density');
    return value === '6' ? '6' : '4';
  }

  function syncDensityButtons() {
    var el = bar();
    if (!el) return;
    var value = currentDensity();
    el.querySelectorAll('[data-cmc-density]').forEach(function (button) {
      button.setAttribute('aria-pressed', button.dataset.cmcDensity === value ? 'true' : 'false');
    });
  }

  function setDensity(value) {
    document.documentElement.setAttribute('data-cmc-density', value === '6' ? '6' : '4');
    try {
      window.localStorage.setItem(DENSITY_KEY, value === '6' ? '6' : '4');
    } catch (e) {
      /* navigation privée : la densité reste valable pour la session. */
    }
    syncDensityButtons();
  }

  /* Le téléphone a sa propre échelle (2 | 1) et sa propre mémoire : le même
     nom pour deux échelles rendrait la valeur ininterprétable au changement
     d'appareil. L'attribut est posé sur `<html>` par le script en ligne du
     snippet, AVANT la première analyse de la grille. */
  function currentDensityM() {
    return document.documentElement.getAttribute('data-cmm-density') === '1' ? '1' : '2';
  }

  function syncDensityButtonsM() {
    var el = bar();
    if (!el) return;
    var value = currentDensityM();
    el.querySelectorAll('button[data-cmm-density]').forEach(function (button) {
      button.setAttribute('aria-pressed', button.dataset.cmmDensity === value ? 'true' : 'false');
    });
  }

  function setDensityM(value) {
    var v = value === '1' ? '1' : '2';
    document.documentElement.setAttribute('data-cmm-density', v);
    try {
      window.localStorage.setItem(DENSITY_KEY_M, v);
    } catch (e) {
      /* navigation privée */
    }
    syncDensityButtonsM();
  }

  /* ── Gestes composés ──────────────────────────────────────────────────── */

  /* Le palier est une valeur de la facette Balises (`taille-groupe:Moyen`), pas un
     paquet de cotes : le compteur affiché est donc exactement le résultat obtenu.
     Shopify combine plusieurs balises en ET, donc deux paliers cochés donneraient
     leur intersection : on décoche les autres avant de soumettre, une seule fois. */
  function applyPalier(input) {
    var form = barForm();
    if (!form) return;

    if (input.checked) {
      form.querySelectorAll('input[data-cmc-palier]').forEach(function (other) {
        if (other !== input) other.checked = false;
      });
    }
    submitForm();
  }

  function toggleValue(name, value) {
    var form = barForm();
    if (!form) return false;

    var inputs = form.querySelectorAll('input[name="' + name.replace(/"/g, '\\"') + '"]');
    var found = null;
    inputs.forEach(function (input) {
      if (input.value === value) found = input;
    });
    if (!found) return false;

    found.checked = !found.checked;
    submitForm();
    return true;
  }

  function setPrice(min, max) {
    var form = barForm();
    if (!form) return;

    var minInput = form.querySelector('input[name="filter.v.price.gte"]');
    var maxInput = form.querySelector('input[name="filter.v.price.lte"]');
    if (!minInput && !maxInput) return;

    if (minInput) minInput.value = min > 0 ? String(min) : '';
    if (maxInput) maxInput.value = max > 0 ? String(max) : '';
    /* Une tranche remplit les deux champs d'un coup : si une frappe attendait
       encore son tour, elle décrirait un prix que l'utilisateur vient de
       remplacer. */
    cancelPrice();
    submitForm();
  }

  function clearGroups(names, alsoPaliers) {
    var form = barForm();
    if (!form) return;

    var touched = false;
    var uncheck = function (input) {
      if (input.checked) {
        input.checked = false;
        touched = true;
      }
    };

    names.forEach(function (name) {
      if (!name) return;
      form.querySelectorAll('input[name="' + name.replace(/"/g, '\\"') + '"]').forEach(uncheck);
    });
    if (alsoPaliers) form.querySelectorAll('input[data-cmc-palier]').forEach(uncheck);

    if (touched) submitForm();
  }

  /* 19/09/2026 · `plus` : noms en sus, séparés par une espace
     (`data-cmc-clear-plus`, panneau Propriétés : Chauffage au sol, Robot aspirateur). */
  function clearGroup(name, plus) {
    clearGroups([name].concat((plus || '').split(' ')), false);
  }

  /* ── VAGUE 7, point 8 a · les champs min / max attendent 500 ms ────────────
     Les deux champs sont des `<input type="number">` DANS le formulaire de
     facettes, et `facets.js` soumet le formulaire à chaque `change` qui remonte
     jusqu'à lui. Une flèche de compteur maintenue enfoncée, ou cinq clics rapides
     sur « + », c'était donc cinq requêtes de rendu de section - cinq fois ~1 Mo,
     cinq fondus de grille qui se chevauchent, et le « flash » décrit par Raph. La
     dernière réponse arrivée gagnait, ce qui n'est même pas forcément la dernière
     demandée.

     On intercepte `input` ET `change` EN CAPTURE, au niveau du document : la
     propagation s'arrête là, l'écouteur du formulaire (qui est sur le formulaire,
     donc en phase de bouillonnement) ne voit jamais rien, et on soumet UNE fois
     500 ms après la dernière frappe. `submitForm()` diffuse son `change` sur le
     FORMULAIRE lui-même, dont la cible n'est pas un champ de prix : il n'est donc
     pas ré-intercepté, et il n'y a aucune boucle.

     L'attente est signalée par l'état de chargement DÉJÀ EN PLACE
     (`data-cmc-fetching`, § 12.2 de la feuille de style) : les compteurs
     s'estompent dès la première frappe, et pas seulement quand la requête part.
     Sans cela, une demi-seconde s'écoulerait sans que rien ne bouge à l'écran. */
  var PRICE_DEBOUNCE = 500;
  var priceTimer = 0;

  function isPriceField(node) {
    return !!(
      node &&
      node.classList &&
      node.classList.contains('cmc-field__input') &&
      node.closest &&
      node.closest('casemira-filter-bar')
    );
  }

  function cancelPrice() {
    if (!priceTimer) return;
    window.clearTimeout(priceTimer);
    priceTimer = 0;
  }

  function schedulePrice() {
    window.clearTimeout(priceTimer);
    fetching(true);
    priceTimer = window.setTimeout(function () {
      priceTimer = 0;
      submitForm();
    }, PRICE_DEBOUNCE);
  }

  ['input', 'change'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        if (!isPriceField(event.target)) return;
        event.stopPropagation();
        schedulePrice();
      },
      true
    );
  });

  function clearPrice() {
    var form = barForm();
    if (!form) return;

    var minInput = form.querySelector('input[name="filter.v.price.gte"]');
    var maxInput = form.querySelector('input[name="filter.v.price.lte"]');
    var touched = false;
    [minInput, maxInput].forEach(function (input) {
      if (input && input.value !== '') {
        input.value = '';
        touched = true;
      }
    });
    cancelPrice();
    if (touched) submitForm();
  }

  function openDrawer() {
    var drawer = document.getElementById('FacetDrawer');
    if (!drawer) return;
    if (typeof drawer.show === 'function') {
      drawer.show();
    } else {
      drawer.hidden = false;
      drawer.setAttribute('open', '');
    }
  }

  /* ======================================================================
     LA FEUILLE MOBILE (vague 2)
     ----------------------------------------------------------------------
     Elle n'a pas de DOM à elle : c'est `.cmc__sticky`, la même boîte que la
     barre de l'ordinateur, remise en scène par le CSS sous 1000 px. Ce
     script ne fait donc que trois choses de plus qu'en vague 1 :
       1. tenir trois états sur `<html>` (ouverte / cran / verrou), parce
          que `<html>` est le seul nœud que le rendu AJAX ne remplace pas ;
       2. traduire le glissement du doigt en changement de cran ou en
          fermeture, avec les seuils du lecteur vidéo de la fiche ;
       3. rendre à la feuille, après chaque rendu AJAX, ce que le
          remplacement du DOM lui a pris : ses groupes dépliés et sa
          position de défilement.
     Les filtres, eux, s'appliquent EN ARRIÈRE-PLAN à chaque tuile touchée,
     exactement comme sur ordinateur : « Voir les N tapis » ne fait que
     fermer et ramener en haut de la grille.
     ====================================================================== */

  var root = document.documentElement;

  function isMobileUi() {
    return !isDesktop() && !!bar();
  }

  function sheetEl() {
    var el = bar();
    return el ? el.querySelector('[data-cmm-sheet]') : null;
  }

  /* Le panneau blanc, celui qui porte le fond, les angles et le `transform` :
     c'est lui qui capture le pointeur, pas la scène plein écran, qui est
     transparente aux doigts (`pointer-events: none`). */
  function panelEl() {
    var s = sheetEl();
    return s ? s.querySelector('.cmc__bar') : null;
  }

  function scrollEl() {
    var el = bar();
    return el ? el.querySelector('[data-cmm-scroll]') : null;
  }

  /* ── Verrou de défilement : `overflow` sur la racine, jamais `position:
     fixed` sur `body`, qui perdrait la position de lecture. Copie conforme du
     verrou du lecteur vidéo (assets/casemira-videos.js). ─────────────────── */
  function lockScroll() {
    var sbw = window.innerWidth - root.clientWidth;
    root.style.setProperty('--cmm-sbw', sbw + 'px');
    root.classList.add('cmm-locked');
  }

  function unlockScroll() {
    root.classList.remove('cmm-locked');
    root.style.removeProperty('--cmm-sbw');
  }

  /* ── Le bouton retour du téléphone ────────────────────────────────────────
     Sans cela, le premier réflexe d'un utilisateur pris dans une feuille plein
     écran (retour Android, glissement depuis le bord sur iOS) le fait QUITTER
     la collection. On empile donc un état à l'ouverture.

     Particularité qui n'existait pas dans le lecteur vidéo : `facets.js`
     appelle `history.replaceState()` à chaque filtre, donc il réécrit l'URL DE
     NOTRE entrée. Un retour ramènerait l'URL d'avant l'ouverture alors que la
     grille, elle, est filtrée. On répare en reposant la dernière URL rendue
     tout de suite après la dépile. */
  var sheetRestoration = null; /* valeur de `history.scrollRestoration` à rendre */
  var sheetPopExpected = false; /* une dépile déclenchée par NOUS est en route */

  function pushHistory() {
    if (sheetPushed) return;
    try {
      /* Toute fermeture dépile notre entrée, et le navigateur restaurerait
         alors la position de lecture MÉMORISÉE avec l'entrée précédente - donc
         il annulerait la remontée vers le haut de la grille que « Voir les N
         tapis » vient de demander. On lui retire ce réflexe le temps que la
         feuille vit, et on le lui rend juste après. */
      if ('scrollRestoration' in window.history) {
        sheetRestoration = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';
      }
      window.history.pushState({ cmmSheet: true }, '');
      sheetPushed = true;
    } catch (e) {
      sheetPushed = false; /* navigateur qui refuse : les autres sorties restent */
    }
  }

  function popHistory() {
    if (!sheetPushed) return;
    sheetPushed = false;
    sheetPopExpected = true;
    try {
      window.history.back();
    } catch (e) {
      sheetPopExpected = false;
    }
  }

  function restoreRestoration() {
    if (sheetRestoration === null) return;
    try {
      window.history.scrollRestoration = sheetRestoration;
    } catch (e) {
      /* rien à récupérer */
    }
    sheetRestoration = null;
  }

  window.addEventListener('popstate', function () {
    var ours = sheetPushed || sheetPopExpected;
    if (sheetPushed) {
      sheetPushed = false;
      if (sheetCran) closeSheet(true);
    }
    sheetPopExpected = false;
    if (!ours) return;

    restoreRestoration();

    /* `facets.js` réécrit l'URL de l'entrée courante à chaque filtre
       (`history.replaceState`) : celle où l'on atterrit décrit donc la page
       AVANT l'ouverture de la feuille, alors que la grille, elle, est filtrée.
       On repose la dernière URL réellement rendue : URL et grille redeviennent
       d'accord, quel que soit le geste qui a fermé la feuille. */
    if (lastRenderedUrl && window.location.href !== lastRenderedUrl) {
      try {
        window.history.replaceState({}, '', lastRenderedUrl);
      } catch (e) {
        /* l'URL restera celle d'avant l'ouverture : la grille est juste, elle */
      }
    }
  });

  /* ── Les groupes dépliés ────────────────────────────────────────────────── */

  function defaultGroups() {
    var el = bar();
    if (!el) return [];
    return SHEET_OPEN_BY_DEFAULT.filter(function (key) {
      return !!el.querySelector(PANEL_SELECTOR + '[data-cmc-panel="' + key + '"]');
    });
  }

  function applyGroups() {
    var el = bar();
    if (!el) return;
    if (!sheetGroups) sheetGroups = defaultGroups();

    el.querySelectorAll(PANEL_SELECTOR).forEach(function (panel) {
      var on = sheetGroups.indexOf(panel.dataset.cmcPanel) !== -1;
      panel.classList.toggle('is-open', on);
      /* Les styles en ligne du placement d'ordinateur n'ont aucun sens ici. */
      panel.style.removeProperty('inset-inline-start');
      panel.style.removeProperty('max-height');
    });
    el.querySelectorAll('[data-cmc-toggle]').forEach(function (button) {
      var on = sheetGroups.indexOf(button.dataset.cmcToggle) !== -1;
      button.classList.toggle('is-open', on);
      button.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
  }

  function toggleGroup(key) {
    if (!sheetGroups) sheetGroups = defaultGroups();
    var i = sheetGroups.indexOf(key);
    if (i === -1) {
      sheetGroups.push(key);
    } else {
      sheetGroups.splice(i, 1);
    }
    applyGroups();
  }

  /* ── Ouverture, crans, fermeture ────────────────────────────────────────── */

  /* Le bouton « Filtrer » de la barre collante (vague 9, point 2) porte le même
     `data-cmm-open` que la pilule qu'il remplace : tout ce qui l'ouvrait,
     l'annonçait et lui rendait le focus continue de fonctionner sans une ligne
     de plus. */
  function syncBar() {
    var el = bar();
    if (!el) return;
    var open = el.querySelector('[data-cmm-open]');
    if (open) open.setAttribute('aria-expanded', sheetCran ? 'true' : 'false');
  }

  function setCran(n) {
    sheetCran = n;
    root.classList.toggle('cmm-cran-2', n === 2);
  }

  function openSheet() {
    if (!isMobileUi() || sheetCran) return;
    closePanel(); /* rien de l'ordinateur ne doit rester ouvert */
    setCran(1);
    if (!sheetGroups) sheetGroups = defaultGroups();
    applyGroups();
    root.classList.add('cmm-open');
    lockScroll();
    pushHistory();
    syncBar();
  }

  function closeSheet(fromHistory) {
    if (!sheetCran) return;
    var sheet = sheetEl();
    var scroller = scrollEl();
    if (scroller) sheetScrollTop = scroller.scrollTop;

    setCran(0);
    /* `cmm-away` avec : la feuille se ferme sur une remontée vers les résultats,
       et la barre doit être là quand on y arrive, pas cachée par un état qui
       datait d'avant l'ouverture (vague 7, point 16 ; vague 9, point 2). */
    root.classList.remove('cmm-open');
    /* VAGUE 21 · une feuille qui part emporte le geste avec elle, et le geste
       ne vit plus sur la racine : `dragRest` retire la classe de la SCÈNE, la
       propriété et le `will-change` du PANNEAU. Appelée ici tant que la
       feuille est encore dans l'arbre. */
    dragRest();
    /* Par `barAway()` et non par `classList` : l'état est mémorisé
       (`barHidden`) pour ne jamais reposer une classe déjà posée, et une remise
       à zéro qui court-circuite la fonction laisserait la mémoire en désaccord
       avec le DOM. */
    barAway(false);
    unlockScroll();
    syncBar();
    if (!fromHistory) popHistory();
    /* Si l'empilement avait échoué, aucune dépile ne viendra rendre au
       navigateur son réflexe de restauration : on le lui rend ici. */
    if (!sheetPopExpected) restoreRestoration();

    /* Le focus revient sur le bouton « Filtrer » de la barre, sinon il resterait
       dans une feuille devenue invisible. */
    var el = bar();
    var opener = el ? el.querySelector('[data-cmm-open]') : null;
    if (opener && sheet && sheet.contains(document.activeElement)) opener.focus({ preventScroll: true });
  }

  /* « Voir les N tapis » : les filtres sont DÉJÀ appliqués, il ne reste qu'à
     sortir et à ramener l'utilisateur là où il verra le résultat - la rangée de
     chips actives, puis la première rangée de la grille. */
  function applyAndClose() {
    closeSheet();

    /* Polish 26/08 · la mesure et la remontée attendent UNE trame.
       `closeSheet()` retire `cmm-locked`, donc `overflow: hidden` sur la
       racine. Mesurer et lancer `scrollTo` dans la même tâche, c'est le faire
       sur une page que le navigateur n'a pas encore rendue défilable : sur une
       machine chargée, la remontée douce partait et s'arrêtait en route
       (relevé le 26/08 sur le preview bridé : haut de grille à 696 px pour une
       fenêtre de 844, au lieu des 420 px attendus). Une trame suffit, et elle
       ne coûte rien quand tout va vite. */
    requestAnimationFrame(function () {
      scrollToResults();
    });
  }

  function scrollToResults() {
    var el = bar();
    /* Vague 9 : `.cmm-line` n'est plus rendue que s'il y a quelque chose à
       effacer, et `.cmm-bar` est COLLANTE - la mesurer donnerait sa position
       collée et non sa place dans le flux. Restent la rangée de chips et la
       grille, qui sont l'une et l'autre dans le flux. */
    var candidates = [
      el ? el.querySelector('.cmc__chips') : null,
      document.getElementById('ProductGridContainer'),
    ];
    var target = null;
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i] && candidates[i].offsetHeight > 0) {
        target = candidates[i];
        break;
      }
    }
    if (!target) return;

    /* L'en-tête se ré-épingle pendant la remontée : on lui réserve sa hauteur,
       comme le fait déjà `facets.js` dans `afterRenderSection()`.

       VAGUE 9, point 2 : la BARRE COLLANTE se réserve aussi. Elle vit avant la
       rangée de chips dans le flux, donc elle se pose sous l'en-tête au moment
       où la remontée s'arrête - et sans ces 44 px, elle recouvrirait exactement
       la rangée qu'on venait montrer. On réserve sa hauteur qu'elle soit
       retirée ou non : une remontée vers les résultats la fait revenir. */
    var header = parseFloat(getComputedStyle(root).getPropertyValue('--header-height')) || 0;
    var barre = 0;
    if (isMobileUi()) {
      var bel = barEl();
      if (bel) barre = bel.getBoundingClientRect().height || 0;
    }
    var top = target.getBoundingClientRect().top + window.scrollY - header - barre - 12;
    var max = Math.max(0, root.scrollHeight - window.innerHeight);

    window.scrollTo({
      top: Math.min(Math.max(0, top), max),
      behavior: isReduced() ? 'auto' : 'smooth',
    });
  }

  /* ── VAGUE 9, point 2 · LA BARRE SUIT L'EN-TÊTE ────────────────────────────
     La pilule flottante a disparu, et avec elle son arrivée différée
     (`cmm-ready`), son hystérésis de 12 px et son minuteur de repos de 600 ms.
     Ce n'est pas une simplification pour la forme : les trois existaient pour
     tenir une règle de défilement qui RESSEMBLAIT à celle de l'en-tête du thème
     sans en être une, et la vague 8 a passé un point de retour entier à leur
     apprendre à ne plus clignoter.

     Le brief demande une barre « synchronisée avec le header collant du thème
     (même seuil/hystérésis) ». La façon la plus sûre d'avoir le même seuil et
     la même hystérésis que l'en-tête n'est pas de les recopier - deux copies
     divergent au premier réglage changé dans l'éditeur de thème - c'est de lire
     SA décision. `assets/header.js` la pose en toutes lettres sur le `<body>` :
     `header-pinned` quand l'en-tête est visible, absente quand il s'est retiré.
     Sa règle, pour mémoire : on remonte, il revient tout de suite ; on descend,
     il ne part qu'au bout de 200 px cumulés dans le même sens ; à moins de
     100 px sous l'en-tête, il reste.

     Trois nuances, et pas une de plus :

       1. EN TÊTE DE PAGE, ELLE RESTE. `header-pinned` n'est jamais posée tant
          qu'on n'a pas dépassé l'en-tête : sans cette nuance, la barre serait
          cachée au chargement, ce qui est exactement l'inverse du brief
          (« toujours visible en haut de page »).
       2. ELLE NE SE RETIRE QUE COLLÉE. Tant qu'elle est encore dans le flux, à
          400 px du haut de l'écran, la retirer ferait disparaître un objet que
          personne ne regarde et qui ne gêne rien. On ne la retire donc qu'une
          fois posée sous l'en-tête. La question se lit sur sa position : collée,
          son bord haut est exactement à son `top` calculé.
       3. PAS D'EN-TÊTE COLLANT, PAS DE RETRAIT. Si le réglage `sticky_header`
          du thème passe à « none », il n'y a plus de décision à suivre - et une
          barre qui se retirerait sans en-tête pour la couvrir laisserait un
          trou. Elle reste alors visible en permanence.

     L'état vit sur `<html>` (`cmm-away`), le seul nœud que le rendu AJAX ne
     remplace pas, et la feuille de style n'anime que `transform` et `opacity`. */
  var BAR_TOP_ZONE = 120;
  var barTicking = false;
  var barHidden = false;

  function barEl() {
    var el = bar();
    return el ? el.querySelector('[data-cmm-bar]') : null;
  }

  function barAway(on) {
    on = !!on;
    if (on === barHidden) return;
    barHidden = on;
    root.classList.toggle('cmm-away', on);
  }

  /* Collée ou non. Le `top` est un `calc()` sur les variables d'en-tête du
     thème : `getComputedStyle` le résout en pixels, il n'y a donc rien à
     recopier ici non plus. Retirée, la barre est 44 px plus haut que son `top` -
     la comparaison reste vraie, et c'est ce qu'on veut : une barre retirée est
     une barre qui ÉTAIT collée. */
  function barStuck() {
    var el = barEl();
    if (!el) return false;
    var top = parseFloat(window.getComputedStyle(el).top);
    if (!isFinite(top)) top = 0;
    return el.getBoundingClientRect().top <= top + 1;
  }

  function barFrame() {
    barTicking = false;
    stickyFrame();
    if (!isMobileUi() || sheetCran) return;

    if (window.scrollY <= BAR_TOP_ZONE) {
      barAway(false);
      return;
    }
    if (!document.querySelector('[data-sticky-type]')) {
      barAway(false);
      return;
    }
    if (!barStuck()) {
      barAway(false);
      return;
    }
    barAway(!document.body.classList.contains('header-pinned'));
  }

  function barSchedule() {
    if (barTicking) return;
    barTicking = true;
    requestAnimationFrame(barFrame);
  }

  window.addEventListener('scroll', barSchedule, { passive: true });

  /* ── VAGUE 11, point 1 · LA BARRE D'ORDINATEUR SUIT L'EN-TÊTE ──────────────
     La feuille de style fait tout le travail : `top` fixe à la hauteur du groupe
     d'en-tête, et un `transform` de -142 px quand l'en-tête s'est retiré, dans
     la durée et la courbe de l'en-tête lui-même (`--animation-nav`). Il ne reste
     ici qu'une chose à dire, et une seule : LA BARRE EST-ELLE COLLÉE ?

     POURQUOI LA QUESTION EST NÉCESSAIRE. Un `transform` s'applique à un élément
     collant qu'il soit collé ou non. Sans garde, la barre encore dans le flux -
     à quatre cents pixels du haut de l'écran, en pleine grille - remonterait de
     142 px dès que l'en-tête se retire, c'est-à-dire dès 282 px de défilement,
     bien avant d'avoir atteint son point de collage. Elle viendrait chevaucher
     le bandeau. Mesuré, pas supposé : le point de collage de la barre est à
     660 px sur `tapis-salon` à 1440 px de large.

     POURQUOI UN TÉMOIN, ET PAS LA BARRE ELLE-MÊME. La méthode du reste du
     fichier (`barStuck`, vague 9) compare le rectangle de l'objet à son `top`
     calculé. Elle vaut pour la barre du téléphone, qui se retire d'un bloc et
     dont le décalage est connu ; elle ne vaut plus ici, où le `transform` est
     EN TRAIN de s'animer pendant 500 ms : à mi-course, le rectangle ment de
     soixante-dix pixels et la réponse se met à osciller. Un témoin de hauteur
     nulle, glissé juste avant la barre, n'est ni collant ni transformé : sa
     position est celle du flux, à chaque image, quoi qu'il arrive à la barre.

     Il est posé par le script et non par le Liquid pour une raison de contrôle :
     servi en local (`CMC_LOCAL=1`), il est éprouvé pour de bon avant le push.
     Il ne peint rien, ne prend aucune place et sort de l'arbre d'accessibilité.

     L'état vit sur `<html>` (`cmc-collee`), comme `cmm-away` : le seul nœud que
     le rendu AJAX de `#FacetFiltersContainer` ne remplace pas. */
  var temoin = null;

  function stickyEl() {
    var el = bar();
    return el ? el.querySelector('.cmc__sticky') : null;
  }

  /* Le témoin suit la barre : après un rendu AJAX, la barre est un nœud neuf et
     le témoin d'avant a disparu avec son parent. On le repose. */
  function temoinPose() {
    var el = stickyEl();
    if (!el || !el.parentNode) return null;
    if (temoin && temoin.parentNode === el.parentNode && temoin.nextSibling === el) return temoin;
    temoin = document.createElement('div');
    temoin.className = 'cmc__temoin';
    temoin.setAttribute('aria-hidden', 'true');
    temoin.style.cssText = 'display:block;height:0;margin:0;padding:0;border:0;pointer-events:none;';
    el.parentNode.insertBefore(temoin, el);
    return temoin;
  }

  function stickyFrame() {
    if (!isDesktop()) {
      root.classList.remove('cmc-collee');
      return;
    }
    var el = stickyEl();
    if (!el) {
      root.classList.remove('cmc-collee');
      return;
    }
    /* Pas d'en-tête collant, pas de retrait à suivre : la barre reste où le
       `top` la met, et aucune classe n'est posée. */
    if (!document.querySelector('[data-sticky-type]')) {
      root.classList.remove('cmc-collee');
      return;
    }
    var t = temoinPose();
    if (!t) {
      root.classList.remove('cmc-collee');
      return;
    }
    /* `top` est un `calc()` sur les variables d'en-tête du thème :
       `getComputedStyle` le résout en pixels, il n'y a rien à recopier. */
    var top = parseFloat(window.getComputedStyle(el).top);
    if (!isFinite(top)) top = 0;
    root.classList.toggle('cmc-collee', t.getBoundingClientRect().top <= top + 1);
  }

  /* `header.js` bascule `header-pinned` dans SA propre `requestAnimationFrame` :
     selon l'ordre des écouteurs, notre image peut passer juste avant la sienne
     et lire l'état d'avant. On réagit donc aussi au changement de classe, ce qui
     rend la synchronisation exacte au lieu d'exacte à une image près. */
  if (window.MutationObserver) {
    new MutationObserver(barSchedule).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /* ── VAGUE 7, point 15 · la rangée de chips dit qu'elle défile ─────────────
     Le fondu de 24 px du bord droit est un masque posé par la classe
     `cmc__chips--fade` (voir la feuille de style, § 3). Il n'apparaît QUE quand
     il reste quelque chose à droite : rangée qui tient dans la largeur, ou
     rangée arrivée au bout, pas de fondu. Un signal qui ment est pire que pas de
     signal - c'est déjà la règle du tiroir de formats de la fiche produit.

     Le seuil de 8 px, et non 0 : les largeurs de défilement sont fractionnaires
     sur les écrans à densité non entière, et `scrollWidth - clientWidth` y garde
     un reste d'un pixel ou deux même au bout de la course. */
  var CHIPS_FADE_SLOP = 8;

  /* ── VAGUE 9, point 4 · la barre de défilement des chips ────────────────────
     La flèche « › » de la vague 8 disait « il y a une suite ». La barre dit en
     plus COMBIEN il en reste et OÙ l'on en est - c'est la même information que
     celle d'iOS, dans la même forme, et elle REMPLACE la flèche au lieu de s'y
     ajouter (le brief : « retire la flèche si elle fait doublon »).

     Elle est mesurée dans la MÊME fonction que le fondu, et c'est le point : les
     deux disent la même chose de la même rangée, et deux fonctions séparées
     finiraient par se contredire d'une image. Le calcul vit dans une
     `requestAnimationFrame` armée au plus une fois par image, comme celui de la
     pilule de la vague 8 : iOS émet `scroll` jusqu'à 120 fois par seconde
     pendant une inertie.

     Le curseur ne bouge qu'en `transform` : sa largeur est reposée à chaque
     mesure, mais aucune transition ne la porte, donc rien ne repasse par la mise
     en page pendant le glissement.

     VAGUE 10, point 6 · LE PLANCHER DE 24 px N'A PLUS D'OBJET. La piste ne fait
     plus la largeur de l'écran mais 28 px, et le curseur une longueur FIXE de
     12 px : ce n'est plus une échelle (12 sur 28 ne dirait rien de juste sur une
     rangée de trente chips), c'est un repère qui parcourt une course. La
     proportion est portée par la POSITION, de 0 à 16 px, et elle, elle est
     exacte.

     ★ VAGUE 11, point 3 · IL NE S'EN VA PLUS, ET IL EST LÀ DÈS LE RENDU.
     La vague 10 lui donnait deux sorties : la rangée menée jusqu'au bout, ou
     4 s après son allumage. Retour de Raph du 27/08 : « il n'apparaît pas
     toujours et disparaît après un défilement vers la droite ». Les deux
     défauts n'en font qu'un seul avec l'auto-effacement, et le premier a une
     seconde cause, mesurée :

       · IL DISPARAISSAIT parce qu'on le lui avait demandé. La règle « un
         indicateur apprend puis n'apprend plus rien » vaut pour une invite qui
         se lit une fois ; elle ne vaut pas pour une POSITION dans une liste, qui
         reste vraie tant que la liste défile. Le curseur d'iOS, dont celui-ci
         reprend la forme, ne s'efface pas non plus tant qu'on défile. Les deux
         sorties partent, avec le minuteur et la classe `is-done`.
       · IL N'APPARAISSAIT PAS TOUJOURS parce que la seule mesure faite au
         chargement tombait AVANT que les polices ne soient posées. Une rangée de
         chips mesurée en police de repli est plus étroite de quelques dizaines
         de pixels : `scrollWidth - clientWidth` valait zéro, la piste restait
         éteinte, et plus rien ne la rallumait - il n'y a ni défilement ni
         redimensionnement à l'arrivée sur la page. On mesure donc AUSSI quand
         les polices sont prêtes, et un `ResizeObserver` posé sur la rangée
         reprend la main à chaque changement de largeur, d'où qu'il vienne.

     ET LA COURSE EST BORNÉE. iOS laisse `scrollLeft` sortir de ses gonds pendant
     l'élastique de fin de course (valeurs négatives, ou supérieures à la
     course) : sans bornes, le curseur sortait de sa piste puis y revenait d'un
     coup au relâchement. C'est le « saut au relâchement » du retour. La position
     est gardée au dixième de pixel, pas à l'unité : sur une course de 16 px,
     l'arrondi à l'entier se voyait comme un cliquetis. */
  var CHIPS_TRACK = 28;
  var CHIPS_THUMB = 12;
  var chipsTicking = false;
  var chipsRO = null;
  var chipsWatched = null;

  function chipsEl() {
    var el = bar();
    return el ? el.querySelector('.cmc__chips') : null;
  }

  /* La rangée est un nœud neuf après chaque rendu AJAX : l'observateur la suit.
     Un seul observateur pour toute la page, réarmé sur la rangée courante. */
  function chipsWatch(chips) {
    if (!window.ResizeObserver || chipsWatched === chips) return;
    if (!chipsRO) chipsRO = new ResizeObserver(chipsSchedule);
    else chipsRO.disconnect();
    chipsWatched = chips;
    chipsRO.observe(chips);
    /* La largeur du CONTENU compte autant que celle de la boîte : une chip qui
       s'élargit quand sa police arrive ne change pas la taille de la rangée. */
    if (chips.firstElementChild) chipsRO.observe(chips.firstElementChild);
    if (chips.lastElementChild && chips.lastElementChild !== chips.firstElementChild) {
      chipsRO.observe(chips.lastElementChild);
    }
  }

  function syncChips() {
    chipsTicking = false;
    var chips = chipsEl();
    if (!chips) return;
    chipsWatch(chips);

    var course = chips.scrollWidth - chips.clientWidth;
    var pos = Math.min(Math.max(chips.scrollLeft, 0), Math.max(course, 0));
    var rest = course - pos;
    chips.classList.toggle('cmc__chips--fade', rest > CHIPS_FADE_SLOP);

    var wrap = chips.parentNode;
    var track = wrap && wrap.querySelector ? wrap.querySelector('.cmc__chipsb') : null;
    if (!track) return;

    /* Même vérité que le fondu, et la SEULE condition qui reste : pas de
       débordement, pas de piste ; débordement, piste allumée, sans limite de
       temps et sans état à retenir. */
    var deborde = course > CHIPS_FADE_SLOP;
    track.classList.toggle('is-on', deborde);
    if (!deborde) return;

    var x = Math.round((pos / course) * (CHIPS_TRACK - CHIPS_THUMB) * 10) / 10;
    track.style.setProperty('--cmc-sb-x', x + 'px');
  }

  function chipsSchedule() {
    if (chipsTicking) return;
    chipsTicking = true;
    requestAnimationFrame(syncChips);
  }

  /* Les polices web arrivent après le premier rendu : c'est la mesure qui
     manquait. Une seule promesse pour la page, elle ne se rejoue pas. */
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(chipsSchedule).catch(function () {
      /* polices refusées : la mesure d'arrivée et l'observateur suffisent */
    });
  }

  document.addEventListener(
    'scroll',
    function (event) {
      var target = event.target;
      if (!target || target.nodeType !== 1 || !target.classList) return;
      if (target.classList.contains('cmc__chips')) chipsSchedule();
    },
    true
  );

  window.addEventListener('resize', chipsSchedule, { passive: true });
  /* Le point de collage de la barre change avec la largeur (le bandeau et la
     grille se remettent en page) : on redemande une image. */
  window.addEventListener('resize', barSchedule, { passive: true });

  /* ── Le glissement ────────────────────────────────────────────────────────
     Deux points de départ : la poignée et l'en-tête (toujours), et le corps
     défilant quand il est déjà en haut (le geste naturel pour refermer). Vers
     le haut depuis le premier cran, on passe en plein écran ; vers le bas, on
     redescend d'un cran puis on ferme. Les seuils sont ceux du lecteur vidéo. */
  /* ── VAGUE 21, 30/08/2026 · LE GESTE ÉTRANGLÉ ────────────────────────────
     Retour de Raph le 30/08 à 14 h 50, site publié, iPhone : la fermeture au
     glissement « saccade ». Fable a compté sur son enregistrement d'écran à
     60 i/s : pendant le glissement lent, le bord haut de la feuille ne bouge
     qu'UNE image sur CINQ - quatre images strictement identiques entre chaque
     pas de 2 à 10 px, soit 12 Hz. Ce n'est pas le téléphone.

     LA CAUSE, MESURÉE AVANT DE TOUCHER À QUOI QUE CE SOIT. Relevé du 30/08 à
     15 h 08 sur le preview réel du thème de travail, glissement de 600 ms
     poussé à 60 Hz sur une horloge murale : WebKit rendait 34 images mais ne
     montrait que **12 positions DISTINCTES** (Chromium bridé x4 : 14 sur 34),
     avec exactement la signature de la vidéo - deux ou trois images
     identiques, puis un saut. Et le nombre qui explique tout : **un pas de
     geste coûtait 35 ms en WebKit** (28,8 ms en Chromium bridé) pour un
     budget d'image de 16,7 ms. Le navigateur ne pouvait pas suivre ; il
     n'avait pas le choix.

     POURQUOI UN PAS COÛTAIT DEUX IMAGES. `--cmm-dy` était posée sur `<html>`.
     Une propriété personnalisée est HÉRITÉE : la reposer sur la racine
     invalide le style de TOUS les nœuds du document - la grille et ses
     trente-six cartes, la barre de filtres, le pied - à chaque `pointermove`,
     pour déplacer UN SEUL élément. La classe `cmm-drag` vivait au même
     endroit et coûtait le même prix.

     C'EST EXACTEMENT LE DÉFAUT QUE LA VAGUE 19 AVAIT CORRIGÉ SUR LA
     MINI-FICHE, et sa note est toujours écrite dans
     `casemira-collection-plus.js` (« le geste n'écrit que sur la feuille »).
     La correction n'avait simplement jamais traversé jusqu'ici : les deux
     feuilles ont été écrites à deux moments différents, et seule la seconde a
     été soignée. La propriété et la classe descendent donc sur les deux seuls
     éléments que le geste concerne, le PANNEAU et la SCÈNE.

     TROIS AUTRES CHOSES MANQUAIENT, ET CHACUNE A SA LIGNE ICI.

     1. LA VITESSE ÉTAIT MOYENNÉE SUR TOUT LE GESTE (`event.timeStamp -
        dragT0`). Un geste qui hésite puis part d'un coup rendait une vitesse
        très inférieure au seuil et ne fermait pas. La mini-fiche avait corrigé
        ce défaut mot pour mot à la vague 20 ; la feuille de filtres, non. La
        vitesse se lit maintenant sur les 90 dernières millisecondes, ce qui
        est la définition d'un lancer.
     2. AUCUN `touchmove` NON PASSIF N'EXISTAIT. `preventDefault()` sur un
        `pointermove` n'annule AUCUN défilement : sur WebKit, la seule chose
        qui arrête le défilement natif et le rebond élastique est un
        `touchmove` non passif. La feuille faisait donc deux mouvements à la
        fois, le nôtre et celui de Safari. Même correction qu'à la vague 20.
     3. RIEN NE PRÉVENAIT LE COMPOSITEUR. `will-change: transform` est posée
        pendant le geste et RETIRÉE à la fin : la laisser en permanence garde
        une couche en mémoire pour rien.

     CE QUI N'A PAS CHANGÉ : les seuils (80 px ou un geste vif, 6 px de tap,
     40 px vers le haut pour le plein écran, 240 px pour fermer depuis le plein
     écran, élastique de 56 px), les deux crans, et le contenu de la feuille.  */

  var dragging = false;
  var dragArmed = false;
  var dragId = null;
  var dragY0 = 0;
  var dragT0 = 0;
  var dragMoved = false;

  /* La traînée : deux points suffisent, le plus ancien encore dans la fenêtre
     et le dernier. Aucun tableau qui grandit, aucune lecture de géométrie. */
  var dragTrail = [];

  function dragSample(y, t) {
    dragTrail.push({ y: y, t: t });
    /* On jette ce qui est plus vieux que la fenêtre, mais jamais au point de
       n'avoir plus qu'un point : deux sont le minimum pour qu'une vitesse
       existe. */
    while (dragTrail.length > 2 && t - dragTrail[0].t > DRAG_WINDOW) dragTrail.shift();
  }

  function dragSpeed(y, t) {
    var a = dragTrail.length ? dragTrail[0] : { y: dragY0, t: dragT0 };
    return (y - a.y) / Math.max(1, t - a.t);
  }

  function dragOffset(dy) {
    if (dy >= 0) return dy;
    /* Vers le haut : élastique court, la feuille ne monte pas plus haut que
       son cran, elle indique seulement qu'il y en a un autre. */
    if (sheetCran === 2) return Math.max(-DRAG_RUBBER, dy / 3);
    return Math.max(-DRAG_RUBBER, dy / 2);
  }

  /* ── LES TROIS ÉCRITURES DU GESTE, ET ELLES NE TOUCHENT QUE LA FEUILLE ───
     `dragOn` arme, `dragMove` déplace, `dragRest` repose. Le déplacement est
     un `transform` et rien d'autre : aucune propriété de mise en page n'est
     touchée, et RIEN N'EST LU pendant le mouvement, donc aucune reprise de
     mise en page forcée. */
  function dragOn() {
    var s = sheetEl();
    var pan = panelEl();
    if (s) s.classList.add('cmm-drag');
    if (pan) pan.style.willChange = 'transform';
  }

  function dragMove(dy) {
    var pan = panelEl();
    if (pan) pan.style.setProperty('--cmm-dy', Math.round(dy) + 'px');
  }

  function dragRest() {
    var s = sheetEl();
    var pan = panelEl();
    if (s) s.classList.remove('cmm-drag');
    if (pan) {
      pan.style.removeProperty('--cmm-dy');
      pan.style.willChange = '';
    }
  }

  /* ── LE DÉPART, ÉCRIT UNE FOIS POUR LES DEUX SOURCES D'ÉVÉNEMENTS ───────
     Il rend `true` quand la feuille prend le geste en charge. Même découpage
     que la mini-fiche (`qdBegin` / `qdConsider` / `qdStep` / `qdFinish`) :
     les deux surfaces se manipulent de la même main, et se lisent de la
     même façon. */
  function dragBegin(target, y, t, id, capture) {
    if (dragging) return true;
    /* Remise à zéro inconditionnelle : un glissement qui a FERMÉ la feuille ne
       laisse pas de `click` derrière lui, et le drapeau resterait armé pour le
       tap suivant - celui de la pilule, justement. */
    dragMoved = false;
    if (!sheetCran || !isMobileUi()) return false;
    if (!target || typeof target.closest !== 'function') return false;

    var onGrab = !!target.closest('[data-cmm-grab]');
    var scroller = scrollEl();
    var onBody = !onGrab && scroller && scroller.contains(target);
    if (!onGrab && !onBody) return false;
    if (onBody && scroller.scrollTop > 0) return false;
    if (target.closest('select')) return false;

    dragging = true;
    dragArmed = onGrab; /* depuis la poignée, le geste est immédiat */
    dragId = id;
    dragY0 = y;
    dragT0 = t;
    dragTrail.length = 0;
    dragSample(y, t);
    if (onGrab) {
      dragOn();
      if (capture) {
        try {
          panelEl().setPointerCapture(id);
        } catch (e) {
          /* capture refusée : le geste marche quand même, en moins précis */
        }
      }
    }
    return true;
  }

  /* La décision. Elle rend `true` quand le geste est le nôtre, c'est-à-dire
     quand il faut à la fois déplacer la feuille ET interdire à Safari de faire
     autre chose. */
  function dragConsider(y, capture) {
    if (!dragging) return false;
    if (dragArmed) return true;
    var dy = y - dragY0;
    /* Depuis le corps, on n'accapare le geste qu'une fois le seuil de tap
       franchi ET seulement vers le bas : un défilement vers le haut reste un
       défilement. */
    if (dy <= DRAG_SLOP) {
      if (dy < -DRAG_SLOP) dragging = false;
      return false;
    }
    var scroller = scrollEl();
    if (scroller && scroller.scrollTop > 0) {
      dragging = false;
      return false;
    }
    dragArmed = true;
    dragOn();
    if (capture) {
      try {
        panelEl().setPointerCapture(dragId);
      } catch (e) {
        /* idem */
      }
    }
    return true;
  }

  function dragStep(y, t, capture) {
    if (!dragConsider(y, capture)) return false;
    var dy = y - dragY0;
    if (Math.abs(dy) > DRAG_SLOP) dragMoved = true;
    dragSample(y, t);
    dragMove(dragOffset(dy));
    return true;
  }

  /* La fin. `close` est vrai pour un relâchement, faux pour une annulation :
     une annulation repose la feuille et ne décide rien. */
  function dragFinish(y, t, close) {
    if (!dragging) return;
    var armed = dragArmed;
    dragging = false;
    dragArmed = false;

    try {
      if (dragId !== null && panelEl()) panelEl().releasePointerCapture(dragId);
    } catch (e) {
      /* déjà relâché */
    }
    dragId = null;
    /* Le retour élastique EST le retrait de `--cmm-dy` : la transition du
       panneau reprend dans la même image et ramène le `transform` à zéro.
       Rien à animer à la main, rien à programmer. */
    dragRest();
    if (!armed || !close) {
      dragTrail.length = 0;
      return;
    }

    var dy = y - dragY0;
    var v = dragSpeed(y, t);
    dragTrail.length = 0;
    var fast = Math.abs(v) > DRAG_VELOCITY;

    if (dy < 0 && (dy <= -DRAG_UP || fast) && sheetCran === 1) {
      setCran(2);
      return;
    }
    if (dy > 0 && (dy >= DRAG_DISTANCE || fast)) {
      if (sheetCran === 2 && dy < DRAG_DISMISS) {
        setCran(1);
      } else {
        closeSheet();
      }
    }
  }

  function dragAbort() {
    if (!dragging) return;
    dragging = false;
    dragArmed = false;
    dragId = null;
    dragTrail.length = 0;
    dragRest();
  }

  /* ── LE CHEMIN DU DOIGT ─────────────────────────────────────────────────
     Il est prioritaire sur celui du pointeur : c'est le seul qui peut dire non
     au défilement natif. `touchstart` en capture pour être servi avant tout
     écouteur de la page, `touchmove` NON PASSIF pour avoir le droit d'appeler
     `preventDefault()`.

     DEUX DOIGTS NE GLISSENT PAS UNE FEUILLE : c'est un pincement, et il
     appartient au navigateur (le zoom de page reste possible, règle
     d'accessibilité). Le geste en cours est abandonné, la feuille revient. */
  document.addEventListener(
    'touchstart',
    function (event) {
      if (event.touches.length > 1) {
        dragAbort();
        return;
      }
      var t = event.touches[0];
      dragBegin(event.target, t.clientY, event.timeStamp, 'touch', false);
    },
    true
  );

  document.addEventListener(
    'touchmove',
    function (event) {
      if (!dragging) return;
      if (event.touches.length > 1) {
        dragAbort();
        return;
      }
      var t = event.touches[0];
      if (!dragStep(t.clientY, event.timeStamp, false)) return;
      /* C'est ICI, et nulle part ailleurs, que le défilement natif et le rebond
         élastique de WebKit sont arrêtés. */
      if (event.cancelable) event.preventDefault();
    },
    { passive: false, capture: true }
  );

  document.addEventListener(
    'touchend',
    function (event) {
      if (!dragging) return;
      var t = event.changedTouches[0];
      dragFinish(t ? t.clientY : dragY0, event.timeStamp, true);
    },
    true
  );

  document.addEventListener('touchcancel', dragAbort, true);

  /* ── LE CHEMIN DU POINTEUR ──────────────────────────────────────────────
     Il reste pour la souris et le stylet, et pour les navigateurs qui
     n'émettent pas d'événements tactiles. Quand le doigt a déjà pris le geste
     en charge, `dragBegin` rend `true` sans rien refaire et `dragStep` est
     idempotent : les deux chemins ne peuvent pas déplacer la feuille deux
     fois. */
  document.addEventListener(
    'pointerdown',
    function (event) {
      if (event.pointerType === 'touch' && dragging) return;
      dragBegin(event.target, event.clientY, event.timeStamp, event.pointerId, true);
    },
    true
  );

  document.addEventListener('pointermove', function (event) {
    if (!dragging || event.pointerId !== dragId) return;
    dragStep(event.clientY, event.timeStamp, true);
  });

  function endDrag(event) {
    if (!dragging) return;
    dragFinish(event.clientY, event.timeStamp, event.type === 'pointerup');
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  /* La position de défilement de la feuille est relevée en continu : le rendu
     AJAX peut arriver à n'importe quel moment et détruire le nœud qui la
     porte. `scroll` ne remonte pas, d'où l'écoute en capture. */
  document.addEventListener(
    'scroll',
    function (event) {
      var target = event.target;
      if (!target || target.nodeType !== 1 || !target.hasAttribute) return;
      if (target.hasAttribute('data-cmm-scroll')) sheetScrollTop = target.scrollTop;
    },
    true
  );

  /* Après un rendu AJAX, la feuille est un nœud tout neuf : on lui rend ses
     groupes dépliés et sa position de défilement dans la même frame, avant la
     première peinture, pour que rien ne saute sous les yeux. */
  function restoreSheet() {
    if (!isMobileUi()) return;
    applyGroups();
    syncDensityButtonsM();
    syncBar();
    if (!sheetCran) return;

    var scroller = scrollEl();
    if (!scroller || !sheetScrollTop) return;
    scroller.scrollTop = sheetScrollTop;
    requestAnimationFrame(function () {
      var again = scrollEl();
      if (again && sheetCran) again.scrollTop = sheetScrollTop;
    });
  }

  /* ── VAGUE 8, point 2 · un panneau ouvert se ferme au clic N'IMPORTE OÙ ────
     CE QUI NE MARCHAIT PAS, ET POURQUOI. La vague 1 fermait le panneau depuis
     l'écouteur de clic de la barre, dans sa toute première ligne :
     `if (!target.closest('casemira-filter-bar')) closePanel()`. Deux trous, et
     ils couvrent précisément ce que Raph a cliqué :

       · LES TUILES DE TAILLE ET LA BARRE ELLE-MÊME sont DANS
         `<casemira-filter-bar>` - les tuiles y sont rendues depuis la vague 4,
         pour que `facets.js` recalcule leurs compteurs avec le reste de la
         barre (voir le commentaire du snippet). Un clic sur une tuile, sur le
         fond de la barre ou sur le compteur « 490 tapis » tombait donc dans la
         branche « dedans », traversait toutes les conditions sans en satisfaire
         aucune, et ne fermait rien ;
       · ET LE MÉCANISME ÉTAIT FRAGILE : l'écouteur était en phase de BULLE,
         donc un `stopPropagation()` posé n'importe où sur le trajet suffisait à
         annuler la fermeture. Le thème en pose déjà (`theme.js`,
         `handlePosterClick` ; `facets.js`, les deux bornes de prix), et rien
         n'empêche une application tierce d'en poser demain sur un bloc de la
         page.

     LA CORRECTION. La fermeture devient un écouteur SÉPARÉ, en phase de
     CAPTURE, posé sur le document : il voit tous les clics de la page avant
     qu'un quelconque `stopPropagation()` puisse les retenir, et il ne connaît
     qu'une question - le clic est-il dans le panneau ouvert, ou sur le bouton
     qui l'a ouvert ? Sinon, on ferme. La barre, les tuiles, le bandeau, la
     grille, l'en-tête, le pied de page : tout le reste ferme.

     Le bouton de l'ouverture est exclu pour une raison mécanique : sans cela,
     la capture fermerait le panneau, puis la bulle rouvrirait le même panneau
     (`openPanelKey === key` ne serait plus vrai) et le second clic sur un
     bouton n'aurait plus jamais l'effet d'une bascule.

     Échap est déjà traité plus bas, et ne change pas. */
  document.addEventListener(
    'click',
    function (event) {
      if (!openPanelKey) return;
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var el = bar();
      if (!el) {
        openPanelKey = null;
        return;
      }

      var panel = el.querySelector(PANEL_SELECTOR + '[data-cmc-panel="' + openPanelKey + '"]');
      if (panel && panel.contains(target)) return;

      var button = el.querySelector('[data-cmc-toggle="' + openPanelKey + '"]');
      if (button && button.contains(target)) return;

      closePanel();
    },
    true
  );

  /* ── Écouteurs délégués, posés une seule fois ─────────────────────────── */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    /* La fermeture au clic dehors ne vit plus ici : elle a son propre écouteur,
       en phase de capture, juste au-dessus (vague 8, point 2). */
    var inside = target.closest('casemira-filter-bar');
    if (!inside) return;

    /* ── Commandes propres au téléphone ──────────────────────────────────── */
    if (target.closest('[data-cmm-open]')) {
      event.preventDefault();
      openSheet();
      return;
    }

    if (target.closest('[data-cmm-close]')) {
      event.preventDefault();
      closeSheet();
      return;
    }

    if (target.closest('[data-cmm-apply]')) {
      event.preventDefault();
      applyAndClose();
      return;
    }

    var densityM = target.closest('button[data-cmm-density]');
    if (densityM) {
      event.preventDefault();
      setDensityM(densityM.dataset.cmmDensity);
      return;
    }

    /* Un glissement retenu se termine aussi par un `click` : ce n'est pas un
       tap, il ne doit rien déclencher. Même garde que le lecteur vidéo. */
    if (dragMoved) {
      dragMoved = false;
      event.preventDefault();
      return;
    }

    var toggle = target.closest('[data-cmc-toggle]');
    if (toggle) {
      event.preventDefault();
      var key = toggle.dataset.cmcToggle;
      /* Dans la feuille, plusieurs groupes peuvent rester ouverts en même
         temps (Taille ET Prix, brief point 3) : ce n'est pas le même geste
         qu'un panneau ancré, qui est exclusif par construction. */
      if (!isDesktop()) {
        toggleGroup(key);
      } else if (openPanelKey === key) {
        closePanel();
      } else {
        openPanel(key);
      }
      return;
    }

    var chip = target.closest('[data-cmc-check]');
    if (chip) {
      event.preventDefault();
      if (toggleValue(chip.dataset.cmcCheck, chip.dataset.cmcValue)) {
        /* Retour visuel dans la même image, avant le recompte : la case cochée
           est invisible (elle vit dans un panneau ou dans le seau), c'est donc
           au contrôle de se peindre lui-même. Le rendu AJAX qui suit repose
           l'état depuis Liquid, il ne peut pas y avoir de désaccord durable. */
        var on = chip.getAttribute('aria-pressed') !== 'true';
        chip.classList.toggle('is-on', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      return;
    }

    var tranche = target.closest('[data-cmc-price-min]');
    if (tranche) {
      event.preventDefault();
      var min = parseInt(tranche.dataset.cmcPriceMin, 10) || 0;
      var max = parseInt(tranche.dataset.cmcPriceMax, 10) || 0;
      var wasOn = tranche.getAttribute('aria-pressed') === 'true';
      if (wasOn) {
        clearPrice();
      } else {
        setPrice(min, max);
      }
      /* Idem : les tranches sont des boutons, rien ne les repeint tant que le
         rendu n'est pas revenu. Une seule tranche à la fois. */
      var group = tranche.parentElement;
      if (group) {
        group.querySelectorAll('[data-cmc-price-min]').forEach(function (other) {
          other.classList.remove('is-on');
          other.setAttribute('aria-pressed', 'false');
        });
      }
      if (!wasOn) {
        tranche.classList.add('is-on');
        tranche.setAttribute('aria-pressed', 'true');
      }
      return;
    }

    var clearTaille = target.closest('[data-cmc-clear-taille]');
    if (clearTaille) {
      event.preventDefault();
      clearGroups([clearTaille.dataset.cmcClearTaille], true);
      return;
    }

    var clear = target.closest('[data-cmc-clear]');
    if (clear) {
      event.preventDefault();
      clearGroup(clear.dataset.cmcClear, clear.dataset.cmcClearPlus);
      return;
    }

    if (target.closest('[data-cmc-clear-price]')) {
      event.preventDefault();
      clearPrice();
      return;
    }

    var disclosure = target.closest('[data-cmc-disc]');
    if (disclosure) {
      event.preventDefault();
      var body = document.getElementById(disclosure.getAttribute('aria-controls'));
      var expanded = disclosure.getAttribute('aria-expanded') === 'true';
      disclosure.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (body) body.hidden = expanded;
      return;
    }

    var more = target.closest('[data-cmc-more]');
    if (more) {
      event.preventDefault();
      var wrapper = more.closest('.cmc-disc__body');
      if (wrapper) wrapper.classList.toggle('is-all');
      return;
    }

    var density = target.closest('button[data-cmc-density]');
    if (density) {
      event.preventDefault();
      setDensity(density.dataset.cmcDensity);
      return;
    }

    if (target.closest('[data-cmc-drawer]')) {
      event.preventDefault();
      closePanel();
      openDrawer();
    }
  });

  /* Les cases de palier n'ont PAS de `name` : elles ne doivent donc pas
     déclencher la soumission du formulaire telles quelles. On intercepte en
     phase de capture - donc avant l'écouteur `change` du formulaire - pour
     poser d'abord les cotes du palier, puis soumettre une seule fois. */
  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      if (!target || !target.hasAttribute || !target.hasAttribute('data-cmc-palier')) return;
      if (!target.closest('casemira-filter-bar')) return;

      event.stopPropagation();
      applyPalier(target);
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    if (sheetCran) {
      closeSheet();
      return;
    }
    if (!openPanelKey) return;

    var el = bar();
    var button = el ? el.querySelector('[data-cmc-toggle="' + openPanelKey + '"]') : null;
    closePanel();
    if (button) button.focus();
  });

  var placeScheduled = false;
  function replaceOpenPanel() {
    if (!openPanelKey || placeScheduled) return;
    placeScheduled = true;
    requestAnimationFrame(function () {
      placeScheduled = false;
      var el = bar();
      var panel = el ? el.querySelector(PANEL_SELECTOR + '[data-cmc-panel="' + openPanelKey + '"]') : null;
      if (panel) placePanel(panel);
    });
  }

  window.addEventListener('resize', replaceOpenPanel, { passive: true });
  /* La barre monte quand elle se colle : le panneau gagne de la place, il faut
     la lui rendre. Rien n'est mesuré tant qu'aucun panneau n'est ouvert. */
  window.addEventListener('scroll', replaceOpenPanel, { passive: true });

  /* ── FLIP : les cartes qui survivent au filtre glissent à leur nouvelle
     place. Celles qui apparaissent ou disparaissent sont couvertes par le
     fondu de #ProductGridContainer posé par facets.js. ─────────────────── */

  function cardKey(column) {
    var link = column.querySelector('a[href*="/products/"]');
    if (!link) return null;
    return link.getAttribute('href').split('?')[0];
  }

  /* ── Polish 26/08 : le compteur pendant le fetch ────────────────────────
     `facets.js` remplace la section entière : entre le clic et l'arrivée du
     HTML, « 490 tapis » et « Voir les 490 tapis » annoncent un nombre qui
     n'est déjà plus vrai. `data-cmc-fetching` sur `<html>` le dit - opacité
     et curseur d'attente, § 12.2 de la feuille de style - et rien d'autre :
     le contenu, lui, reste celui du dernier rendu réussi, ce qui est aussi ce
     qu'il faut montrer si le rendu échoue.

     L'attribut est posé et retiré ici et nulle part ailleurs. Les trois
     événements sont diffusés par `facets.js` sur `document`, et les deux
     sorties (`rerendered`, `renderfailed`) sont exhaustives : aucun chemin ne
     laisse l'attribut derrière lui. */
  function fetching(on) {
    if (on) document.documentElement.setAttribute('data-cmc-fetching', '');
    else document.documentElement.removeAttribute('data-cmc-fetching');
  }

  document.addEventListener('collection:beforererender', function () {
    fetching(true);
  });
  document.addEventListener('collection:renderfailed', function () {
    fetching(false);
  });

  document.addEventListener('collection:beforererender', function () {
    flipMap = null;
    if (!isDesktop() || isReduced()) return;

    var grid = document.getElementById('ProductsList');
    if (!grid) return;

    var height = window.innerHeight;
    flipMap = new Map();
    flipScrollY = window.scrollY;

    grid.querySelectorAll('.f-column').forEach(function (column) {
      var key = cardKey(column);
      if (!key) return;
      var rect = column.getBoundingClientRect();
      /* Inutile de mesurer ce qui est hors de l'écran : on n'anime que ce que
         l'utilisateur a réellement sous les yeux. */
      if (rect.bottom < -200 || rect.top > height + 200) return;
      flipMap.set(key, { x: rect.left, y: rect.top });
    });
  });

  document.addEventListener('collection:rerendered', function () {
    fetching(false);
    syncDensityButtons();
    syncDensityButtonsM();
    /* La rangée de chips est un nœud tout neuf : son fondu et sa piste de
       défilement se recalculent. */
    chipsSchedule();
    /* Dernière URL réellement rendue : c'est elle qu'on repose sur l'entrée
       d'historique si le bouton retour du téléphone ferme la feuille (voir
       `popstate` plus haut). */
    lastRenderedUrl = window.location.href;
    if (!isDesktop()) restoreSheet();

    var previous = flipMap;
    flipMap = null;

    if (refocusKey) {
      var form = barForm();
      if (form) {
        var again = form.querySelector(refocusKey);
        if (again) again.focus({ preventScroll: true });
      }
      refocusKey = null;
    }

    if (!previous || previous.size === 0) return;
    /* facets.js a pu remonter la page : la géométrie d'avant n'a alors plus
       aucun rapport avec celle d'après, on renonce à l'animation. */
    if (Math.abs(window.scrollY - flipScrollY) > 2) return;

    var grid = document.getElementById('ProductsList');
    if (!grid) return;

    var moves = [];
    grid.querySelectorAll('.f-column').forEach(function (column) {
      var key = cardKey(column);
      if (!key) return;
      var before = previous.get(key);
      if (!before) return;

      var rect = column.getBoundingClientRect();
      var dx = before.x - rect.left;
      var dy = before.y - rect.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      moves.push({ el: column, dx: dx, dy: dy });
    });

    if (moves.length === 0) return;

    moves.forEach(function (move) {
      move.el.style.transform = 'translate(' + Math.round(move.dx) + 'px, ' + Math.round(move.dy) + 'px)';
    });

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        moves.forEach(function (move) {
          move.el.classList.add('cmc-flip');
          move.el.style.transform = '';
        });
      });
    });

    window.setTimeout(function () {
      moves.forEach(function (move) {
        move.el.classList.remove('cmc-flip');
        move.el.style.transform = '';
      });
    }, 500);
  });

  /* Mémorise la case en cours pour lui rendre le focus après le re-rendu :
     `renderFacetFiltersContainer` détruit et recrée toute la barre. */
  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.closest('casemira-filter-bar')) return;
      if (!target.name) return;
      refocusKey =
        'input[name="' + target.name.replace(/"/g, '\\"') + '"][value="' + String(target.value).replace(/"/g, '\\"') + '"]';
    },
    true
  );

  /* ── L'élément personnalisé ───────────────────────────────────────────── */

  /* `data-cmc-bar` dit à facets.js : « c'est nous qui tenons cette page ».
     Depuis la vague 2 c'est vrai des deux côtés de la charnière - la grille
     sort en fondu de 150 ms et rentre en 250 ms, sans saut de 50 px ni cascade
     carte par carte, sur téléphone comme sur ordinateur. */
  function markActive() {
    if (bar()) {
      document.documentElement.setAttribute('data-cmc-bar', '');
    } else {
      document.documentElement.removeAttribute('data-cmc-bar');
    }
  }

  if (desktopQuery && desktopQuery.addEventListener) {
    desktopQuery.addEventListener('change', function () {
      markActive();
      if (isDesktop()) {
        /* On passe à l'ordinateur : la feuille n'a plus de sens, son verrou de
           défilement encore moins, et les groupes dépliés de l'accordéon
           deviendraient autant de panneaux flottants. */
        closeSheet();
        root.classList.remove('cmm-cran-2');
        barAway(false);
        closePanel();
      } else {
        /* On passe au téléphone : les styles en ligne posés par `placePanel()`
           (largeur, hauteur maximale) décriraient un panneau ancré qui n'existe
           plus. `applyGroups()` les efface et rouvre Taille et Prix. */
        closePanel();
        applyGroups();
        syncDensityButtonsM();
      }
    });
  }

  class CasemiraFilterBar extends HTMLElement {
    connectedCallback() {
      markActive();
      syncDensityButtons();
      syncDensityButtonsM();
      /* La barre est dans le flux : elle n'a rien à « arriver ». On repose son
         état (elle est visible tant qu'on n'a pas défilé) et on mesure la rangée
         de chips, qui vient d'être recréée. */
      barAway(false);
      requestAnimationFrame(syncChips);
      barSchedule();

      /* Le panneau ouvert avant un rendu AJAX est réouvert dans la foulée :
         l'élément vient d'être créé, la classe est donc posée avant le
         premier rendu et aucune transition ne rejoue. */
      if (openPanelKey && isDesktop()) {
        var key = openPanelKey;
        openPanelKey = null;
        openPanel(key);
      }

      /* Sur téléphone, l'état vit sur `<html>` (classes `cmm-*`), que le rendu
         AJAX ne touche jamais : la feuille est donc déjà à sa place et à son
         cran quand ce nœud paraît. Il ne reste qu'à lui rendre ses groupes
         dépliés et sa position de défilement. */
      if (!isDesktop()) restoreSheet();
    }
  }

  if (!customElements.get('casemira-filter-bar')) {
    customElements.define('casemira-filter-bar', CasemiraFilterBar);
  }
})();
